import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { useBudgetAnalytics } from "@/hooks/useBudgetAnalytics";
import { AnalyticsMetrics } from "@/components/budgets/analytics/AnalyticsMetrics";
import { ConversionFunnel } from "@/components/budgets/analytics/ConversionFunnel";
import { TrendChart } from "@/components/budgets/analytics/TrendChart";
import { StatusDistribution } from "@/components/budgets/analytics/StatusDistribution";
import { PerformanceTable } from "@/components/budgets/analytics/PerformanceTable";
import { AdvancedFilters } from "@/components/budgets/analytics/AdvancedFilters";
import { ExportControls } from "@/components/budgets/analytics/ExportControls";
import {
  BarChart3,
  TrendingUp,
  PieChart,
  Users,
  Filter,
  Download,
  RefreshCw,
} from "lucide-react";

// Function to handle exports
const handleExport = async (type: string, options: any) => {
  // Simulate export process
  console.log(`Exporting ${type} with options:`, options);

  // In a real implementation, this would:
  // 1. Generate PDF with jsPDF and html2canvas
  // 2. Create Excel file with xlsx library
  // 3. Generate CSV data
  // 4. Create PNG screenshot

  return new Promise((resolve) => {
    setTimeout(resolve, 2000);
  });
};

export default function BudgetAnalyticsPage() {
  const [activeTab, setActiveTab] = useState("overview");
  const [showFilters, setShowFilters] = useState(false);

  const {
    data,
    loading,
    error,
    refetch,
    filters,
  } = useBudgetAnalytics();

  const handleFiltersChange = (newFilters: any) => {
    // This would trigger a refetch with new filters
    refetch();
  };

  const handleClearFilters = () => {
    // Clear filters and refetch
    handleFiltersChange({
      startDate: undefined,
      endDate: undefined,
      clientId: undefined,
      categoryId: undefined,
      userId: undefined,
      minValue: undefined,
      maxValue: undefined,
    });
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Análise de Orçamentos</h1>
            <p className="text-muted-foreground">
              Métricas e insights sobre o desempenho dos orçamentos
            </p>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-center space-y-4">
            <div className="text-6xl">😞</div>
            <h3 className="text-lg font-semibold">Erro ao carregar dados</h3>
            <p className="text-muted-foreground max-w-md">
              {error}
            </p>
            <Button onClick={refetch} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Tentar novamente
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Análise de Orçamentos</h1>
          <p className="text-muted-foreground">
            Métricas e insights sobre o desempenho dos orçamentos
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            {showFilters ? 'Ocultar' : 'Mostrar'} Filtros
          </Button>
          <ExportControls
            data={data}
            filters={filters}
            onExport={handleExport}
          />
          <Button onClick={refetch} variant="outline" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <AdvancedFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onClearFilters={handleClearFilters}
          loading={loading}
        />
      )}

      {/* Loading State */}
      {loading && !data && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
        </div>
      )}

      {/* Main Content */}
      {data && (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Visão Geral
            </TabsTrigger>
            <TabsTrigger value="funnel" className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Funil
            </TabsTrigger>
            <TabsTrigger value="trends" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Tendências
            </TabsTrigger>
            <TabsTrigger value="status" className="flex items-center gap-2">
              <PieChart className="h-4 w-4" />
              Status
            </TabsTrigger>
            <TabsTrigger value="performance" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Performance
            </TabsTrigger>
            <TabsTrigger value="services" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Serviços
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <AnalyticsMetrics data={data.overview} loading={loading} />

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 bg-muted/30 rounded-lg">
                <h4 className="font-medium mb-2">Resumo do Período</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Orçamentos Criados</span>
                    <span className="font-medium">{data.overview.totalBudgets}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Valor Total</span>
                    <span className="font-medium">
                      {data.overview.totalValue.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Ticket Médio</span>
                    <span className="font-medium">
                      {data.overview.averageTicket.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      })}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="font-medium mb-2 text-green-800">Melhor Performance</h4>
                <div className="space-y-1 text-sm text-green-700">
                  <div className="flex justify-between">
                    <span>Taxa de Conversão</span>
                    <span className="font-medium">{data.overview.conversionRate.toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Crescimento</span>
                    <span className="font-medium text-green-600">
                      +{data.overview.growth.conversion.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium mb-2 text-blue-800">Oportunidades</h4>
                <div className="space-y-1 text-sm text-blue-700">
                  <div className="flex justify-between">
                    <span>Orçamentos Enviados</span>
                    <span className="font-medium">
                      {data.statusDistribution.find(s => s.status === 'sent')?.count || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Valor Potencial</span>
                    <span className="font-medium">
                      {data.statusDistribution.find(s => s.status === 'sent')?.value.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      }) || 'R$ 0'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <h4 className="font-medium mb-2 text-orange-800">Ação Necessária</h4>
                <div className="space-y-1 text-sm text-orange-700">
                  <div className="flex justify-between">
                    <span>Orçamentos Expirados</span>
                    <span className="font-medium">
                      {data.statusDistribution.find(s => s.status === 'expired')?.count || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Valor Perdido</span>
                    <span className="font-medium">
                      {data.statusDistribution.find(s => s.status === 'expired')?.value.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      }) || 'R$ 0'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Funnel Tab */}
          <TabsContent value="funnel" className="space-y-6">
            <ConversionFunnel data={data.conversionMetrics} loading={loading} />
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends" className="space-y-6">
            <TrendChart data={data.monthlyData} loading={loading} />
          </TabsContent>

          {/* Status Tab */}
          <TabsContent value="status" className="space-y-6">
            <StatusDistribution data={data.statusDistribution} loading={loading} />
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance" className="space-y-6">
            <PerformanceTable data={data.salesPerformance} loading={loading} />
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Services */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Serviços Mais Orçados</h3>
                <div className="space-y-3">
                  {data.topServices.map((service, index) => (
                    <div key={service.name} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-8 h-8 bg-primary text-primary-foreground rounded-full text-sm font-bold">
                            {index + 1}
                          </div>
                          <div>
                            <div className="font-medium">{service.name}</div>
                            <div className="text-sm text-muted-foreground">
                              {service.budgets} orçamentos
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Valor Total: </span>
                          <span className="font-medium">
                            {service.value.toLocaleString('pt-BR', {
                              style: 'currency',
                              currency: 'BRL',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0
                            })}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Ticket Médio: </span>
                          <span className="font-medium">
                            {service.avgTicket.toLocaleString('pt-BR', {
                              style: 'currency',
                              currency: 'BRL',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Service Insights */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Insights de Serviços</h3>
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">Serviço em Destaque</h4>
                    <p className="text-sm text-blue-700">
                      {data.topServices[0]?.name} é o serviço mais procurado, representando a maior oportunidade de receita.
                    </p>
                  </div>

                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="font-medium text-green-900 mb-2">Oportunidade</h4>
                    <p className="text-sm text-green-700">
                      Considere criar pacotes ou promoções para os serviços mais populares para aumentar a conversão.
                    </p>
                  </div>

                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <h4 className="font-medium text-purple-900 mb-2">Recomendação</h4>
                    <p className="text-sm text-purple-700">
                      Foque em melhorar a apresentação dos serviços com ticket médio mais alto para maximizar o valor por orçamento.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}